import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';

import { Source } from './types';

interface Props {
  content: string;
  isLoading: boolean;
  isModelRemote: boolean;
  sources: Source[];
  isStreaming?: boolean;
}

const DEFAULT_TEXT = 'Model response will appear here...';

const ModelOutputPanel = (props: Props) => {
  const { content, isLoading, isModelRemote, sources, isStreaming = false } = props;
  
  const isDefaultText = content === DEFAULT_TEXT;

  if (isLoading) {
    return (
      <OutputContainer $warningBorder={isModelRemote} $isDefaultText={true}>
        Loading...
      </OutputContainer>
    );
  }

  if (isDefaultText) {
    return (
      <OutputContainer $warningBorder={isModelRemote} $isDefaultText={true}>
        <div className="response">
          {content}
        </div>
      </OutputContainer>
    );
  }

  return (
    <OutputContainer $warningBorder={isModelRemote}>
      <div className="response">
        <ReactMarkdown
          components={{
            // Custom styling for markdown elements
            code: ({ children, className }) => {
              const isInline = !className;
              return isInline ? (
                <code style={{
                  backgroundColor: '#f4f4f4',
                  padding: '0.1em 0.3em',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                }}>
                  {children}
                </code>
              ) : (
                <pre style={{
                  backgroundColor: '#f4f4f4',
                  padding: '0.5em',
                  borderRadius: '5px',
                  overflow: 'auto',
                  margin: '0.5em 0'
                }}>
                  <code style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {children}
                  </code>
                </pre>
              );
            },
            blockquote: ({ children }) => (
              <blockquote style={{
                borderLeft: '3px solid #ccc',
                margin: '0.5em 0',
                paddingLeft: '1em',
                fontStyle: 'italic',
                color: '#666'
              }}>
                {children}
              </blockquote>
            ),
            a: ({ children, href }) => (
              <a href={href} style={{ color: '#0066cc', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            // Add a custom component for the last paragraph to include the cursor
            p: ({ children, ...props }) => {
              // Check if this is the last paragraph (contains the cursor)
              const isLastParagraph = isStreaming && typeof children === 'string' && children.endsWith('|');
              if (isLastParagraph) {
                return (
                  <p {...props} style={{ display: 'inline' }}>
                    {children.slice(0, -1)}
                    <span className="typing-cursor">|</span>
                  </p>
                );
              }
              return <p {...props}>{children}</p>;
            },
          }}
        >
          {isStreaming ? `${content}|` : content}
        </ReactMarkdown>
      </div>
      {sources.length > 0 && (
        <div>
          <div style={{ fontWeight: 'bold', marginTop: 8 }}>Sources:</div>
          <ul style={{ paddingLeft: 16 }}>
            {sources.map((doc, idx) => (
              <li key={idx} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: '0.95em', color: '#555' }}>
                  <b>{doc.source}</b>
                  <span style={{ fontSize: '0.85em', color: '#888', marginLeft: 8 }}>
                    ({doc.doc_id})
                  </span>
                </div>
                <div style={{ fontSize: '0.95em', color: '#333' }}>{doc.text}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </OutputContainer>);
};

const OutputContainer = styled.div<{ $warningBorder: boolean; $isDefaultText?: boolean }>`
  background-color: white;
  border: 1px solid ${props => props.$warningBorder ? '#ff5555' : '#ccc'};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  min-height: 0;
  color: ${props => props.$isDefaultText ? '#aaa' : 'inherit'};

  .typing-cursor {
    animation: blink 1s infinite;
    color: #333;
    font-weight: bold;
  }

  .response > *:first-child {
    margin-top: 0;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

export default ModelOutputPanel;
